import React, { useEffect, useRef, useState } from 'react';
import { caseStudies, caseStudyMetrics, DEMO_METRICS_NOTE } from './caseStudyContent';
import useSEO from '../HireWithUs/Pages/HomePage/useSEO';
import Header from '../HireWithUs/Pages/Header&Footer/Header';
import Footer from '../HireWithUs/Pages/Header&Footer/Footer';

const FRAME = 1080;
const SLIDE_COUNT = 5;

function ScaledFrame({ children }) {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const node = outerRef.current;
    if (!node) return undefined;
    const update = () => {
      const width = node.clientWidth;
      setScale(width > 0 ? width / FRAME : 1);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={outerRef} className="w-full overflow-hidden" style={{ height: FRAME * scale }}>
      <div
        style={{
          width: FRAME,
          height: FRAME,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SlideFrame({ index, label, children }) {
  return (
    <li>
      <ScaledFrame>
        <article
          data-slide={index}
          aria-label={`${label}, slide ${index} of ${SLIDE_COUNT}`}
          className="flex h-[1080px] w-[1080px] flex-col overflow-hidden border border-white/10 bg-[hsl(164_33%_7%)] p-16 text-[hsl(40_20%_96%)]"
        >
          <header className="flex items-center justify-between gap-8">
            <p className="text-2xl font-semibold tracking-wide text-white">SearchJob.ai</p>
            <p className="text-xl font-semibold uppercase tracking-[0.16em] text-[hsl(158_40%_72%)]">
              {String(index).padStart(2, '0')} / 05
            </p>
          </header>
          <div className="mt-10 flex min-h-0 flex-1 flex-col">{children}</div>
        </article>
      </ScaledFrame>
    </li>
  );
}

function Kicker({ children }) {
  return (
    <p className="text-xl font-semibold uppercase tracking-[0.16em] text-[hsl(158_40%_72%)]">
      {children}
    </p>
  );
}

function SlideTitle({ children }) {
  return (
    <h2 className="mt-6 text-[52px] font-semibold leading-[1.12] tracking-[-0.02em] text-white">
      {children}
    </h2>
  );
}

function CoverSlide({ study }) {
  return (
    <SlideFrame index={1} label={`${study.customer} cover`}>
      <Kicker>{study.category}</Kicker>
      <div className={`mt-8 flex items-center justify-center overflow-hidden rounded-3xl bg-white px-10 ${study.logoPlateClassName}`}>
        <img src={study.logoSrc} alt={study.customer} className={study.logoClassName} />
      </div>
      <p className="mt-8 text-xl font-semibold uppercase tracking-[0.16em] text-[hsl(158_40%_72%)]">
        {study.focus}
      </p>
      <h2 className="mt-3 text-7xl font-bold leading-none tracking-[-0.03em] text-white">
        {study.customer}
      </h2>
      <p className="mt-8 text-[40px] font-medium leading-snug text-[hsl(40_20%_96%)]">
        {study.headline}
      </p>
      <p className="mt-6 text-[28px] leading-snug text-[hsl(160_12%_78%)]">{study.context}</p>
    </SlideFrame>
  );
}

function ChallengeSlide({ study }) {
  return (
    <SlideFrame index={2} label={`${study.customer} challenge`}>
      <Kicker>The challenge</Kicker>
      <SlideTitle>{study.challengeTitle}</SlideTitle>
      <p className="mt-8 text-[32px] leading-snug text-[hsl(160_12%_78%)]">{study.challengeBody}</p>
      <ul className="mt-10 space-y-4">
        {study.painPoints.map((point) => (
          <li
            key={point}
            className="rounded-2xl border border-white/10 bg-[hsl(164_22%_11%)] px-8 py-6 text-[30px] font-medium text-white"
          >
            {point}
          </li>
        ))}
      </ul>
    </SlideFrame>
  );
}

function ApproachSlide({ study }) {
  return (
    <SlideFrame index={3} label={`${study.customer} approach`}>
      <Kicker>The approach</Kicker>
      <h2 className="mt-6 text-[44px] font-semibold leading-[1.12] tracking-[-0.02em] text-white">
        {study.approachTitle}
      </h2>
      <p className="mt-6 text-[26px] leading-snug text-[hsl(160_12%_78%)]">{study.approachBody}</p>
      <p className="mt-8 text-lg font-semibold uppercase tracking-[0.14em] text-[hsl(158_40%_72%)]">
        {study.workflowTitle}
      </p>
      <ol className="mt-4 flex flex-wrap gap-3">
        {study.workflow.map((step, stepIndex) => (
          <li
            key={step}
            className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-[22px] font-medium text-white"
          >
            {stepIndex + 1}. {step}
          </li>
        ))}
      </ol>
      {study.workflowNote ? (
        <p className="mt-8 text-[26px] leading-snug text-[hsl(160_12%_78%)]">{study.workflowNote}</p>
      ) : null}
    </SlideFrame>
  );
}

function ResultSlide({ study }) {
  const { items, demo } = caseStudyMetrics(study);
  return (
    <SlideFrame index={4} label={`${study.customer} result`}>
      <Kicker>The result</Kicker>
      <SlideTitle>{study.resultsTitle || 'What the customer supports'}</SlideTitle>
      {items.length > 0 ? (
        <>
          {demo ? (
            <p className="mt-6 text-xl leading-snug text-[hsl(160_12%_74%)]">{DEMO_METRICS_NOTE}</p>
          ) : null}
          <ul className="mt-6 grid grid-cols-3 gap-4">
            {items.map((metric) => (
              <li key={metric.label} className="rounded-2xl border border-white/10 bg-[hsl(164_22%_11%)] px-5 py-6">
                <p className="text-5xl font-semibold tracking-tight text-white">{metric.value}</p>
                <p className="mt-3 text-xl font-medium leading-snug text-[hsl(160_12%_78%)]">{metric.label}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <ul className={`${items.length > 0 ? 'mt-6' : 'mt-8'} space-y-3`}>
        {study.results.map((result) => (
          <li
            key={result}
            className="rounded-2xl border border-white/10 bg-[hsl(164_22%_11%)] px-7 py-4 text-[24px] leading-snug text-white"
          >
            {result}
          </li>
        ))}
      </ul>
    </SlideFrame>
  );
}

function QuoteSlide({ study }) {
  return (
    <SlideFrame index={5} label={`${study.customer} quote`}>
      <Kicker>Customer voice</Kicker>
      <figure className="mt-8 flex min-h-0 flex-1 flex-col">
        <blockquote className="text-[26px] font-medium leading-snug text-white">
          “{study.quote}”
        </blockquote>
        <figcaption className="mt-6 text-xl font-semibold uppercase tracking-[0.14em] text-[hsl(158_40%_72%)]">
          {study.attribution}
        </figcaption>
        <a
          href={study.bookingPath}
          className="mt-auto inline-flex h-14 items-center justify-center self-start rounded-md bg-white px-8 text-[22px] font-semibold text-[hsl(165_37%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
        >
          Experience SearchJob.ai with us
        </a>
      </figure>
    </SlideFrame>
  );
}

function carouselStudy(study) {
  return {
    ...study,
    logoSrc: study.assets.logo,
    logoPlateClassName: study.assets.carouselLogoPlate,
    logoClassName: study.assets.carouselLogoClass,
    challengeTitle: study.challenge.title,
    challengeBody: study.challenge.body,
    approachTitle: study.approach.title,
    approachBody: study.approach.body,
    workflowNote: study.workflowNote.body,
  };
}

export const linkedInCarousels = {
  manest: carouselStudy(caseStudies.manest),
  grandGaming: carouselStudy(caseStudies.grandGaming),
  kinzy: carouselStudy(caseStudies.kinzy),
};

export default function CaseStudyLinkedIn({ study }) {
  useSEO({
    title: `${study.customer} LinkedIn carousel — SearchJob.ai`,
    description: study.headline,
    canonical: `https://searchjob.ai/case-studies/${study.slug}/linkedin`,
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen bg-[hsl(164_33%_7%)] text-[hsl(40_20%_96%)]">
      <Header solid />
      <main className="mx-auto max-w-[720px] px-4 pb-16 pt-24 sm:px-6">
        <a
          href={`/case-studies/${study.slug}`}
          className="text-sm font-medium text-[hsl(160_12%_74%)] underline-offset-4 hover:text-white hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {study.customer} case study
        </a>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          LinkedIn carousel
        </h1>
        <p className="mt-3 text-base leading-relaxed text-[hsl(160_12%_78%)]">
          Five slides from the {study.customer} case study.
        </p>
        <ol className="mt-8 space-y-6">
          <CoverSlide study={study} />
          <ChallengeSlide study={study} />
          <ApproachSlide study={study} />
          <ResultSlide study={study} />
          <QuoteSlide study={study} />
        </ol>
      </main>
      <Footer />
    </div>
  );
}
